import React, {useState} from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import CustomButton from '../commonComponents/CustomButton';
import CustomCheckbox from '../commonComponents/CustomCheckBox';
import { EyWorksDatePicker } from 'eyworks-ui-components';
import { useAppDispatch, useAppSelector } from 'src/app/services/store';
import { canViewMedicineFormsSelector, getTimelineFilters, selectFeaturesEnabled } from 'src/shared/redux-state/src';
import { ACTIVITY_TYPE, DATE_FORMATS } from 'src/shared/config/src';
import { ErrorType, FeatureEnabledType, TimelineStateType } from 'src/shared/types/src';
import { compareAsc, format } from "date-fns";
import { TimelineActions } from 'src/shared/redux-state/src/lib/timeline/TimelineSlice';
import { dateFormatter } from 'src/shared/utils/src';
import {isEqual, sortBy} from 'lodash';
import { useNavigate } from 'react-router-dom'
import { getQueryString, encodeStringForQuery } from 'src/app/utilities/helpers';

type Iprops = {
  isTimelineFilterModal: boolean,
  toggleTimelineFilterModal: () => void
}
const TimelineFilterModal: React.FC<Iprops> = ({ isTimelineFilterModal, toggleTimelineFilterModal  }) => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const timelineFilters: TimelineStateType['filters'] = useAppSelector(getTimelineFilters);
  const canViewMedicineForms: boolean = useAppSelector(canViewMedicineFormsSelector);
  const featuresEnabled: FeatureEnabledType = useAppSelector(selectFeaturesEnabled);

  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>(timelineFilters.activityTypes || []);
  const [errors, setErrors] = useState<ErrorType>({});
  const [selectedFromDate, setSelectedFromDate] = useState<Date | any>(timelineFilters.from);
  const [selectedToDate, setSelectedToDate] = useState<Date | any>(timelineFilters.to);
  const [showFutureEvents, setShowFutureEvents] = useState<boolean>(timelineFilters.showFutureEvents);

  const FILTER_ACTIVITIES = {
    [ACTIVITY_TYPE.OBSERVATION]: 'Observations',
    [ACTIVITY_TYPE.DAILY_DIARY]: 'Daily Diaries',
    [ACTIVITY_TYPE.ACCIDENT_SHEET]: 'Accident/Incident Sheet',
    [ACTIVITY_TYPE.MEDICINE_FORM]: 'Medicine Forms',
    [ACTIVITY_TYPE.REPORTS]: 'Reports',
  }

  const FIELD_MAP = {
    FROM_DATE: 'fromDate',
    TO_DATE: 'toDate'
  };

  const runValidators = () => {
    let errorObj: ErrorType = Object.assign({}, errors);
    setErrors(Object.create({}));

    if (selectedFromDate && !selectedToDate) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.TO_DATE]: {
          isErrored: true,
          errorMsg: 'To date is required'
        }
      }
    }

    if (selectedToDate && !selectedFromDate) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.FROM_DATE]: {
          isErrored: true,
          errorMsg: 'From date is required'
        }
      }
    }

    setErrors(errorObj);

    if (Object.keys(errorObj).length) return false;
    else return true;
  };

  const handleFromDateChange = async (selectedDate: Date) => {
    setSelectedFromDate(selectedDate);
    if (selectedToDate) {
      const compareResult = compareAsc(new Date(selectedDate), new Date(selectedToDate));
      if (compareResult === 1) {
        setErrors({
          ...errors,
          [FIELD_MAP.FROM_DATE]: {
            isErrored: true,
            errorMsg: 'Should be before To date'
          }
        })
      } else {
        let e = JSON.parse(JSON.stringify(errors));
        delete e[FIELD_MAP.FROM_DATE]
        delete e[FIELD_MAP.TO_DATE]
        setErrors(e);
      }
    }
  }

  const handleToDateChange = async (selectedDate: Date) => {
    setSelectedToDate(selectedDate);
    if (selectedFromDate) {
      const compareResult = compareAsc(new Date(selectedDate), new Date(selectedFromDate));
      // if end date is before start date
      if (compareResult === -1) {
        setErrors({
          ...errors,
          [FIELD_MAP.TO_DATE]: {
            isErrored: true,
            errorMsg: 'Should be after From date'
          }
        })
      } else {
        let e = JSON.parse(JSON.stringify(errors));
        delete e[FIELD_MAP.FROM_DATE]
        delete e[FIELD_MAP.TO_DATE]
        setErrors(e);
      }
    }
  }

  const handleCheckboxPress = (activityType: string) => {
    const isActivityTypePresent = selectedActivityTypes.find((type: string) => type === activityType);
    if (isActivityTypePresent) {
      setSelectedActivityTypes(selectedActivityTypes.filter((type: string) => type !== activityType));
    } else {
      setSelectedActivityTypes([...selectedActivityTypes, activityType]);
    }
  }

  const handleClearAll = () => {
    setSelectedFromDate(undefined);
    setSelectedToDate(undefined);
    setSelectedActivityTypes([]);
    setErrors(Object.create({}));
    setShowFutureEvents(false);
  }

  const handleSelectAll = () => {
    setSelectedActivityTypes(Object.keys(FILTER_ACTIVITIES));
  }

  const handleApplyFilter = async () => {

    const validity = runValidators();
    if (!validity) return;
    let isFilterApplied = false;
    if (selectedActivityTypes.length !== 0 || selectedFromDate || selectedToDate || showFutureEvents) {
      isFilterApplied = true;
    }
    if (selectedFromDate != timelineFilters.from || selectedToDate != timelineFilters.to || !isEqual(sortBy(selectedActivityTypes), sortBy(timelineFilters.activityTypes)) || showFutureEvents !== timelineFilters.showFutureEvents) {
      await dispatch(TimelineActions.updateFilters({
        selectedTypes: selectedActivityTypes,
        from: dateFormatter(selectedFromDate, DATE_FORMATS.YMD),
        to: dateFormatter(selectedToDate, DATE_FORMATS.YMD),
        isFilterApplied,
        showFutureEvents
      }));
    }
    toggleTimelineFilterModal()

    const query: string = getQueryString({
      from: selectedFromDate,
      to: selectedToDate,
      activityType: selectedActivityTypes,
      showFutureEvents
    })

    const q = encodeStringForQuery(query);
    if (!q) window.history.replaceState({}, '', window.location.pathname)
    else window.history.replaceState({}, '', `${window.location.pathname}?q=${q}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
  }

  const handleFutureEventsClick = () => {
    setShowFutureEvents(!showFutureEvents);
  }

  return (
    <Modal 
      isOpen={isTimelineFilterModal} 
      toggle={toggleTimelineFilterModal} 
      backdrop={true} 
      scrollable={true}
      fade={false}
      className={`custom-modal-md modal-dialog-bottom modal-dialog-bottom-style`}
      modalClassName={`modal-slide-down`}
    >
      <ModalBody className='mg-t-15'>
        <div className='d-flex justify-content-between align-items-center w-100 border-bottom border-gray-600 pd-b-15 mg-b-15'>
          <p className='fs-16 fw-semibold text-black mb-0'>Apply Filters</p>
          <CustomButton text="Reset" color="link" onClick={handleClearAll} customBtnStyle='custom-btn-link-with-underline' />
        </div>
        <div className='form-group mg-b-25'>
          <div className='fs-14 fw-semibold text-black mg-b-13'>Date:</div>
          <div className='d-flex timeline-range-date-picker'>
            <div className='flex-1'>
              <EyWorksDatePicker
                customDateValue={selectedFromDate ? format( selectedFromDate, 'd MMM yyyy') : 'Not Set'}
                onChange={handleFromDateChange}
                selectedDate={selectedFromDate}
              />
              {errors && errors[FIELD_MAP.FROM_DATE] && errors[FIELD_MAP.FROM_DATE].isErrored ? <span className="form-error">{errors[FIELD_MAP.FROM_DATE].errorMsg}</span> : null}
            </div>
            <span className='date-divider'>-</span>
            <div className='flex-1'>
              <EyWorksDatePicker
                customDateValue={selectedToDate ? format(selectedToDate, 'd MMM yyyy') : 'Not Set'}
                onChange={handleToDateChange}
                selectedDate={selectedToDate}
              />
              {errors && errors[FIELD_MAP.TO_DATE] && errors[FIELD_MAP.TO_DATE].isErrored ? <span className="form-error">{errors[FIELD_MAP.TO_DATE].errorMsg}</span> : null}
            </div>
          </div>
        </div>
        <div className='border-bottom border-gray-600 pd-b-15 mg-b-15'>
          <CustomCheckbox 
            label={'Show Future Events on Timeline'} 
            isChecked={showFutureEvents} 
            onChange={handleFutureEventsClick} 
          />
        </div>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='fs-14 fw-semibold text-black'>
            Type:
          </div>
          <CustomButton text="Select all" color="link" onClick={handleSelectAll} customBtnStyle='custom-btn-link-with-underline' />
        </div>
        {Object.keys(FILTER_ACTIVITIES).map(activityType => {
          return (
            <>{((!canViewMedicineForms && activityType === ACTIVITY_TYPE.MEDICINE_FORM) || (!featuresEnabled?.accidentSheet && activityType === ACTIVITY_TYPE.ACCIDENT_SHEET)) ? null :
              <div className='mg-t-15'>
                <CustomCheckbox label={FILTER_ACTIVITIES[activityType]} isChecked={selectedActivityTypes.includes(activityType)} onChange={() => handleCheckboxPress(activityType)} />
              </div>}
              </>
          )
        })}
        
      </ModalBody>
      <ModalFooter>
        <CustomButton text="Apply" color="primary" onClick={handleApplyFilter} />
      </ModalFooter>
    </Modal>
  );
};

export default TimelineFilterModal;
